import * as React from 'react'
// constants
import { COMMON_FIELDS, PROPERTIES_LISTS } from 'constants/objects'
// context
import { ObjectsAPIContext } from 'containers/ObjectsAndProperties/Edit/layers/API'
// types
import { IField, IPlanType } from 'types/graphql'
// utils
import get from 'lodash/get'
import { v4 as uuid } from 'uuid'

type Props = React.PropsWithChildren<unknown>

interface IContext {
  globalProperties: IField[]
  handleSelectPlanType: (planType: IPlanType) => void
  isDropDisabled: { GLOBAL: boolean; PLAN: boolean }
  onDragEnd: (result: any) => void
  onDragStart: (start: any) => void
  selectedPlanType: IPlanType
  planTypeProperties: IField[]
}

export const ObjectsBLContext = React.createContext<IContext>({} as any)

const ObjectsBL: React.FC<Props> = ({ children }) => {
  const [globalProperties, setGlobalProperties] = React.useState([])
  const [planTypeProperties, setPlanTypeProperties] = React.useState([])
  const [isDropDisabled, setIsDropDisabled] = React.useState({ GLOBAL: false, PLAN: false })
  const [selectedPlanType, setSelectedPlanType] = React.useState<IPlanType>(null)

  const { createField, fields, object, updatePositions } = React.useContext(ObjectsAPIContext)

  const handleSelectPlanType = (planType: IPlanType) => setSelectedPlanType(planType)

  const reorder = (list: Record<string, any>[], startIndex: number, endIndex: number, isPlanType = false) => {
    const reorderedList = [...list]

    const [removed] = reorderedList.splice(startIndex, 1)

    reorderedList.splice(endIndex, 0, removed)
    reorderedList.map((item, i) => ({ ...item, position: i }))

    updatePositions(reorderedList, isPlanType)

    return reorderedList
  }

  const copy = (source, destination, droppableSource, droppableDestination, id: string, isPlanType = false) => {
    const item = source[droppableSource.index]
    const shouldAddParentField = droppableSource.droppableId === PROPERTIES_LISTS.GLOBAL

    createField(item, droppableDestination.index, id, isPlanType, shouldAddParentField)

    const newDestination = [...destination]

    newDestination.splice(droppableDestination.index, 0, { ...item, id: uuid() })
    newDestination.map((item, i) => ({ ...item, position: i }))

    updatePositions(newDestination, isPlanType)

    return newDestination
  }

  // disable drop to specified droppable containers
  const onDragStart = (start) => {
    if (start.source.droppableId === PROPERTIES_LISTS.GLOBAL) {
      const property = globalProperties[start.source.index]
      const isPropertyAlreadyExist = planTypeProperties.some((item) => item.parentFieldId === property.id)

      if (property.standard || isPropertyAlreadyExist) {
        setIsDropDisabled({ ...isDropDisabled, PLAN: true })
      }
    }

    if (start.source.droppableId === PROPERTIES_LISTS.PLAN_TYPE) {
      setIsDropDisabled({ ...isDropDisabled, GLOBAL: true })
    }
  }

  const onDragEnd = (result) => {
    const { source, destination } = result

    setIsDropDisabled({ GLOBAL: false, PLAN: false })

    if (!destination) {
      return
    }

    if (source.droppableId === destination.droppableId) {
      if (destination.droppableId === PROPERTIES_LISTS.GLOBAL) {
        setGlobalProperties((state) => reorder(state, source.index, destination.index))
      }

      if (destination.droppableId === PROPERTIES_LISTS.PLAN_TYPE) {
        setPlanTypeProperties((state) => reorder(state, source.index, destination.index, true))
      }
    }

    if (source.droppableId === PROPERTIES_LISTS.COMMON && destination.droppableId === PROPERTIES_LISTS.GLOBAL) {
      setGlobalProperties((state) => copy(COMMON_FIELDS, state, source, destination, object.id))
    }

    if (source.droppableId === PROPERTIES_LISTS.COMMON && destination.droppableId === PROPERTIES_LISTS.PLAN_TYPE) {
      setPlanTypeProperties((state) =>
        copy(COMMON_FIELDS, state, source, destination, get(selectedPlanType, 'id'), true)
      )
    }

    if (source.droppableId === PROPERTIES_LISTS.GLOBAL && destination.droppableId === PROPERTIES_LISTS.PLAN_TYPE) {
      setPlanTypeProperties((state) =>
        copy(globalProperties, state, source, destination, get(selectedPlanType, 'id'), true)
      )
    }
  }

  React.useEffect(() => {
    const reviewFieldIds = object?.reviewFieldIds || []

    setGlobalProperties(fields.filter((field) => !reviewFieldIds.includes(field.id)))
  }, [fields, object])

  React.useEffect(() => {
    if (selectedPlanType) {
      setPlanTypeProperties(get(selectedPlanType, 'fields'))
    }
  }, [selectedPlanType])

  const value = {
    globalProperties,
    handleSelectPlanType,
    isDropDisabled,
    onDragEnd,
    onDragStart,
    selectedPlanType,
    planTypeProperties
  }

  return <ObjectsBLContext.Provider value={value}>{children}</ObjectsBLContext.Provider>
}

export default ObjectsBL
